/**
 * The town maps — the inside of a kingdom, walked a tile at a time
 * (docs/rpg.md §22).
 *
 * One character per tile, exactly as the continent in `overworld.ts` is
 * authored, and for the same reason: a mill stream is obvious in a block of
 * text and invisible in an array of objects.
 *
 * ```
 *   .  meadow     #  lane        ~  water
 *   f  trees      =  bridge      ^  hillside
 *   ,  green
 * ```
 *
 * Starter Village was surveyed first, and it was the right one to start with:
 * it is where a nine-year-old spends their first hour, and five region cards in
 * a row is the flattest thing that hour contains. A kingdom without a town map
 * loses nothing — `KingdomPage` simply draws the cards, the way all ten did
 * before this file existed — but half a kingdom is worse than none, so a
 * kingdom is surveyed whole or not at all: the village *and* a map for every
 * region in it.
 *
 * **A place is a reference and never a second name.** A region place carries a
 * region id and nothing else; its name, its emoji and its progress are read off
 * the kingdom, so a plate on the map and a card below it cannot disagree.
 *
 * **Terrain is scenery and never a lock.** The pond and the hillside stop a
 * step, so they could quietly fence a region off — and a fenced-off region is
 * indistinguishable, from inside the game, from being told you are not good
 * enough for it. `validateTown` walks this map from the arrival tile on every
 * `npm run content:validate` and fails the build if any place is stranded. Even
 * that proof is a belt over braces: every region here is an ordinary card on
 * the kingdom page, and the map only scrolls to it.
 */

import type { Town } from '@yuan-math/world';
import { STARTER_VILLAGE_TOWN } from './maps/starter-village.ts';
import { NUMBER_TOWN } from './maps/number.ts';
import { FRACTION_TOWN } from './maps/fraction.ts';
import { ALGEBRA_TOWN } from './maps/algebra.ts';
import { GEOMETRY_TOWN } from './maps/geometry.ts';
import { STATISTICS_TOWN } from './maps/statistics.ts';
import { FUNCTION_TOWN } from './maps/function.ts';
import { LINEAR_ALGEBRA_TOWN } from './maps/linear-algebra.ts';
import { TRIGONOMETRY_TOWN } from './maps/trigonometry.ts';
import { CALCULUS_TOWN } from './maps/calculus.ts';

/** Every surveyed kingdom. `townOf` picks one; a kingdom without one is fine. */
export const TOWNS: readonly Town[] = [
  STARTER_VILLAGE_TOWN,
  NUMBER_TOWN,
  FRACTION_TOWN,
  ALGEBRA_TOWN,
  GEOMETRY_TOWN,
  STATISTICS_TOWN,
  LINEAR_ALGEBRA_TOWN,
  FUNCTION_TOWN,
  TRIGONOMETRY_TOWN,
  CALCULUS_TOWN,
];
